// ---------------------------------------------------------------------------
// C2+C3: SEO Analyzer — meta generation, alt text, headings, congruence, scoring
// ---------------------------------------------------------------------------
// Pure functions. Used by Design Copilot sidebar for live SEO scoring.
// C2: Meta title/desc generation, alt text audit, heading hierarchy, congruence.
// C3: SEO score 0-100 with 9 weighted factors.
// ---------------------------------------------------------------------------

interface PuckComponent { type: string; props: Record<string, unknown>; }

export interface SeoFactor {
  id: string;
  name: string;
  weight: number;
  score: number; // 0-1
  issues: string[];
  suggestions: string[];
}

export interface SeoScore {
  total: number; // 0-100
  factors: SeoFactor[];
}

export interface MetaSuggestion {
  title: string; // 60 chars max
  description: string; // 155 chars max
}

export interface AltTextAudit {
  totalImages: number;
  withAlt: number;
  withoutAlt: number;
  suggestions: Array<{ componentIndex: number; suggestedAlt: string }>;
}

export interface HeadingAudit {
  hasH1: boolean;
  h1Count: number;
  hierarchyValid: boolean;
  issues: string[];
}

// --- C2: Meta Generation ---

/** Generate meta title + description from page content */
export function generateMetaSuggestion(components: PuckComponent[]): MetaSuggestion {
  const hero = components.find(c => c.type === "hero" || c.type === "header");
  const heading = String(hero?.props.heading ?? hero?.props.title ?? "");
  const subheading = String(hero?.props.subheading ?? hero?.props.subtitle ?? "");
  const body = components.map(c => String(c.props.body ?? c.props.text ?? "")).filter(Boolean).join(" ");

  // Title: heading-based, max 60 chars
  let title = heading || subheading || "Untitled Page";
  if (title.length > 60) title = title.slice(0, 57) + "...";

  // Description: subheading + body excerpt, max 155 chars
  let description = subheading || body.slice(0, 200);
  if (description.length > 155) description = description.slice(0, 152) + "...";

  return { title, description };
}

/** Audit images for alt text */
export function auditAltText(components: PuckComponent[]): AltTextAudit {
  let totalImages = 0;
  let withAlt = 0;
  const suggestions: AltTextAudit["suggestions"] = [];

  components.forEach((c, i) => {
    // Check for image-containing components
    if (c.props.imageUrl || c.props.src || c.props.image || c.type === "image") {
      totalImages++;
      const alt = c.props.alt ?? c.props.imageAlt;
      if (alt) {
        withAlt++;
      } else {
        const heading = String(c.props.heading ?? c.props.title ?? c.type);
        suggestions.push({ componentIndex: i, suggestedAlt: `Image for ${heading}` });
      }
    }
    // Check items arrays for images
    const items = c.props.items as Array<{ imageUrl?: string; alt?: string; title?: string }> | undefined;
    if (Array.isArray(items)) {
      for (const item of items) {
        if (item.imageUrl) {
          totalImages++;
          if (item.alt) withAlt++; else suggestions.push({ componentIndex: i, suggestedAlt: `Image: ${item.title ?? c.type}` });
        }
      }
    }
  });

  return { totalImages, withAlt, withoutAlt: totalImages - withAlt, suggestions };
}

/** Validate heading hierarchy */
export function auditHeadings(components: PuckComponent[]): HeadingAudit {
  const issues: string[] = [];
  let h1Count = 0;
  let lastLevel = 0;
  let hierarchyValid = true;

  for (const c of components) {
    // Check for headings in props
    if (c.props.heading || c.props.title) {
      const level = c.type === "hero" || c.type === "header" ? 1 : 2;
      if (level === 1) h1Count++;
      if (level > lastLevel + 1 && lastLevel > 0) {
        hierarchyValid = false;
        issues.push(`Heading jump from H${lastLevel} to H${level} (should not skip levels)`);
      }
      lastLevel = level;
    }
    // Check for explicit heading levels
    if (c.type === "heading" && typeof c.props.level === "number") {
      const level = c.props.level as number;
      if (level === 1) h1Count++;
      if (level > lastLevel + 1 && lastLevel > 0) {
        hierarchyValid = false;
        issues.push(`Heading jump from H${lastLevel} to H${level}`);
      }
      lastLevel = level;
    }
  }

  if (h1Count === 0) issues.push("No H1 heading found");
  if (h1Count > 1) issues.push(`${h1Count} H1 headings found (should be exactly 1)`);

  return { hasH1: h1Count > 0, h1Count, hierarchyValid, issues };
}

/** Check headline ↔ body ↔ CTA congruence */
export function checkCongruence(components: PuckComponent[]): { score: number; issues: string[] } {
  const hero = components.find(c => c.type === "hero");
  const cta = components.find(c => c.props.buttonText);
  const heading = String(hero?.props.heading ?? "").toLowerCase();
  const ctaText = String(cta?.props.buttonText ?? "").toLowerCase();
  const bodyTexts = components.map(c => String(c.props.body ?? "").toLowerCase()).join(" ");

  const issues: string[] = [];
  let score = 0.5; // Neutral default

  if (!heading) { issues.push("No headline to check congruence"); return { score: 0.3, issues }; }

  // Extract keywords from heading
  const keywords = heading.split(/\s+/).filter(w => w.length > 3);

  // Check keywords appear in body
  const bodyKeywordHits = keywords.filter(k => bodyTexts.includes(k)).length;
  const bodyCongruence = keywords.length > 0 ? bodyKeywordHits / keywords.length : 0;

  // Check CTA relates to headline
  const ctaRelevant = keywords.some(k => ctaText.includes(k));

  score = (bodyCongruence * 0.6) + (ctaRelevant ? 0.4 : 0);

  if (bodyCongruence < 0.3) issues.push("Body content doesn't reinforce the headline");
  if (!ctaRelevant && ctaText) issues.push("CTA text doesn't relate to the headline promise");

  return { score: Math.min(1, score), issues };
}

// --- C3: SEO Score ---

/** Calculate comprehensive SEO score (0-100) with 9 factors */
export function calculateSeoScore(
  components: PuckComponent[],
  options?: { hasSchema?: boolean; metaTitle?: string; metaDescription?: string; pageUrl?: string },
): SeoScore {
  const meta = generateMetaSuggestion(components);
  const altAudit = auditAltText(components);
  const headingAudit = auditHeadings(components);
  const congruence = checkCongruence(components);

  const factors: SeoFactor[] = [
    // Meta (15)
    {
      id: "meta", name: "Meta Tags", weight: 15,
      score: (options?.metaTitle ? 0.5 : 0) + (options?.metaDescription ? 0.5 : 0),
      issues: [
        ...(!options?.metaTitle ? ["Missing meta title"] : []),
        ...(!options?.metaDescription ? ["Missing meta description"] : []),
      ],
      suggestions: !options?.metaTitle ? [`Suggested title: "${meta.title}"`] : [],
    },
    // Schema.org (12)
    {
      id: "schema", name: "Schema.org", weight: 12,
      score: options?.hasSchema ? 1 : 0,
      issues: options?.hasSchema ? [] : ["No Schema.org markup detected"],
      suggestions: ["Add FAQ, Product, or LocalBusiness schema"],
    },
    // Alt text (12)
    {
      id: "alt", name: "Image Alt Text", weight: 12,
      score: altAudit.totalImages === 0 ? 1 : altAudit.withAlt / altAudit.totalImages,
      issues: altAudit.withoutAlt > 0 ? [`${altAudit.withoutAlt} images missing alt text`] : [],
      suggestions: altAudit.suggestions.map(s => `Add alt: "${s.suggestedAlt}"`).slice(0, 3),
    },
    // Headings (12)
    {
      id: "headings", name: "Heading Hierarchy", weight: 12,
      score: (headingAudit.hasH1 ? 0.5 : 0) + (headingAudit.hierarchyValid ? 0.5 : 0),
      issues: headingAudit.issues,
      suggestions: !headingAudit.hasH1 ? ["Add an H1 heading to your hero section"] : [],
    },
    // Content length (10)
    {
      id: "content_length", name: "Content Length", weight: 10,
      score: (() => {
        const totalText = components.map(c => String(c.props.body ?? c.props.text ?? "")).join(" ").length;
        if (totalText > 1500) return 1;
        if (totalText > 800) return 0.7;
        if (totalText > 300) return 0.4;
        return 0.2;
      })(),
      issues: [],
      suggestions: [],
    },
    // Keyword density (10)
    {
      id: "keywords", name: "Keyword Usage", weight: 10,
      score: 0.6, // Requires external keyword — default to moderate
      issues: [],
      suggestions: ["Define a target keyword for more accurate analysis"],
    },
    // Internal links (8)
    {
      id: "links", name: "Internal Links", weight: 8,
      score: components.some(c => c.props.href || c.props.linkUrl) ? 0.8 : 0.2,
      issues: !components.some(c => c.props.href) ? ["No internal links found"] : [],
      suggestions: ["Add links to related pages"],
    },
    // Images (8)
    {
      id: "images", name: "Image Optimization", weight: 8,
      score: altAudit.totalImages > 0 ? 0.7 : 0.4,
      issues: altAudit.totalImages === 0 ? ["No images on page"] : [],
      suggestions: altAudit.totalImages === 0 ? ["Add relevant images to improve engagement"] : [],
    },
    // Congruence (13)
    {
      id: "congruence", name: "Message Congruence", weight: 13,
      score: congruence.score,
      issues: congruence.issues,
      suggestions: [],
    },
  ];

  const total = Math.round(factors.reduce((s, f) => s + f.score * f.weight, 0));
  return { total, factors };
}
